export const LANGS = ['uz', 'ru', 'en'] as const;
export const DEFAULT_LANG = 'uz';

/** Default number of free stories; the live value is the `freeLimit` setting in the DB. */
export const FREE_LIMIT_DEFAULT = 3;

/** Default evening reminder (Tashkent): kids 2–7 usually go to bed at 21:00–22:00. */
export const BROADCAST_TIME_DEFAULT = '20:30';

export const TIMEZONE = 'Asia/Tashkent';
/** Uzbekistan has no DST, so Tashkent is always UTC+5. */
export const TASHKENT_UTC_OFFSET_MIN = 5 * 60;

export const SESSION_TTL_SEC = 15 * 60;
export const INIT_DATA_MAX_AGE_SEC = 24 * 60 * 60;
export const AUDIO_URL_TTL_SEC = 6 * 60 * 60;
export const LISTEN_HEARTBEAT_SEC = 15;
export const LISTEN_BATCH_MAX = 50;
export const PAYMENT_POLL_MAX_MS = 60_000;

/** Keys of the runtime `Setting` table. */
export const SETTING_KEYS = {
  freeLimit: 'freeLimit',
  broadcastTime: 'broadcastTime',
  defaultPublishTime: 'defaultPublishTime',
  ttsVoices: 'ttsVoices',
  starsEnabled: 'starsEnabled',
  /** Evening broadcast frequency by activity (see NOTIFY_DEFAULTS). */
  broadcastActiveDays: 'broadcastActiveDays',
  broadcastLapsedDays: 'broadcastLapsedDays',
  broadcastLapsedWeekdays: 'broadcastLapsedWeekdays',
  broadcastNewUserDays: 'broadcastNewUserDays',
  /** Subscription notices: time of day and how many days before the end. */
  subNoticeTime: 'subNoticeTime',
  subExpiringDays: 'subExpiringDays',
  dailyReportTime: 'dailyReportTime',
  /** Content pipeline (Phase 5), see CONTENT_DEFAULTS. */
  claudeModelGenerate: 'claudeModelGenerate',
  claudeModelReview: 'claudeModelReview',
  ttsMonthlyCreditLimit: 'ttsMonthlyCreditLimit',
  autoGenerateEnabled: 'autoGenerateEnabled',
  generateTime: 'generateTime',
  reviewMinScore: 'reviewMinScore',
  /** Admin "Stop everything" switch (the CONTENT_PAUSED env is the hard override). */
  contentPaused: 'contentPaused',
  /** Last provider check written by the worker (keys, permissions, credits). */
  systemCheck: 'systemCheck',
  /** Official CBU USD→UZS rate { rate, date, fetchedAt }, refreshed daily. */
  fxUsdUzs: 'fxUsdUzs',
  /** Manual USD→UZS override (number) — wins over the CBU rate when set. */
  fxUsdUzsManual: 'fxUsdUzsManual',
  /** Price of one ElevenLabs credit in USD for the current plan. */
  elevenLabsCreditUsd: 'elevenLabsCreditUsd',
  /** Per-language ElevenLabs voice settings { uz: { stability, style }, … }. */
  ttsVoiceSettings: 'ttsVoiceSettings',
  /** Per-language Claude "director" (audio tags) switch { uz: false, ru: true, en: true }. */
  autoTags: 'autoTags',
} as const;

/**
 * Voice defaults: steady, calm bedtime narration. Stability: higher = steadier, lower = more
 * emotional; style: higher = more expressive.
 */
export const VOICE_SETTINGS_DEFAULT = { stability: 0.5, style: 0.15 };
/**
 * Auto tags start off for Uzbek until the trial voicing shows that tags work there and are not
 * read aloud (owner decision, 2026-10-17).
 */
export const AUTO_TAGS_DEFAULT = { uz: false, ru: true, en: true } as const;

/** Age range of the app; default for generation, forms and review (owner, 2026-10-17). */
export const AGE_MIN = 2;
export const AGE_MAX = 7;

/**
 * Claude prices, USD per million tokens (platform.claude.com/docs/en/about-claude/pricing,
 * checked 2026-10-17; base input / output, no caching).
 */
export const CLAUDE_PRICES: Record<string, { input: number; output: number }> = {
  'claude-opus-5-5': { input: 4, output: 20 },
  'claude-sonnet-5-5': { input: 2, output: 10 },
  'claude-haiku-5-5': { input: 0.1, output: 0.5 },
};

/** ElevenLabs Starter: $5 for 30 000 credits (used until the owner sets the plan price). */
export const ELEVENLABS_CREDIT_USD_DEFAULT = 5 / 30_000;

/**
 * Content pipeline defaults (owner decisions, 2026-10-17): both passes on Claude Opus 5.5;
 * ElevenLabs Starter for tests with a 25 000-credit monthly cap; the 03:00 daily generation is
 * off until the owner switches it on (manual "Generate" only on staging).
 */
export const CONTENT_DEFAULTS = {
  claudeModelGenerate: 'claude-opus-5-5',
  claudeModelReview: 'claude-opus-5-5',
  ttsMonthlyCreditLimit: 25_000,
  autoGenerateEnabled: false,
  generateTime: '03:00',
  /** Review score (0–100) below which a story is flagged (never deleted). */
  reviewMinScore: 70,
};

/**
 * Notification defaults (owner decisions, 2026-10-17); live values are editable settings.
 * Evening broadcast: listened within `broadcastActiveDays` → every evening; last listen
 * `broadcastActiveDays`–`broadcastLapsedDays` ago → only on `broadcastLapsedWeekdays`
 * (ISO weekday, 1 = Mon); longer, or never listened after `broadcastNewUserDays` → nothing until
 * they listen again; brand-new users who have not listened yet → every evening in their first days.
 */
export const NOTIFY_DEFAULTS = {
  broadcastActiveDays: 14,
  broadcastLapsedDays: 30,
  broadcastLapsedWeekdays: [2, 5] as readonly number[],
  broadcastNewUserDays: 7,
  /** Daytime: easier to pay, and it does not stack with the bedtime broadcast. */
  subNoticeTime: '13:00',
  subExpiringDays: 3,
  dailyReportTime: '09:00',
};

// ── Uploaded narration (Phase 5.1) ──
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;
/** Narration shorter / longer than this gets a warning (not a refusal). */
export const DURATION_WARN = { minSec: 3 * 60, maxSec: 15 * 60 };
/** ElevenLabs Scribe v2: $0.22 per hour of audio (elevenlabs.io/pricing/api, checked 2026-10-17). */
export const STT_USD_PER_HOUR = 0.22;
export const UPLOAD_EXTENSIONS = ['mp3', 'wav', 'm4a'] as const;

/** Content queue job names (BullMQ), one processor each in the worker. */
export const CONTENT_JOB_NAMES = [
  'generate',
  'tts',
  'voices',
  'upload',
  'direct',
  'preview',
  'align',
] as const;
export type ContentJobName = (typeof CONTENT_JOB_NAMES)[number];

/**
 * ElevenLabs audio tags allowed in a bedtime TTS script (documented v3/v4 delivery tags + soft
 * descriptive ones; elevenlabs.io/docs/best-practices/prompting/eleven-v3, checked 2026-10-17).
 */
export const BEDTIME_TAGS = [
  'whispers',
  'sighs',
  'exhales',
  'inhales deeply',
  'laughs',
  'giggles',
  'chuckles',
  'curious',
  'excited',
  'mischievously',
  'happy',
  'sad',
  'thoughtful',
  'surprised',
  'short pause',
  'long pause',
  'softly',
  'gently',
  'quietly',
  'warmly',
  'slowly',
  'calmly',
  'sleepily',
] as const;
/** Quick-insert buttons of the script editor, in the order a narrator reaches for them. */
export const QUICK_TAGS = [
  'softly',
  'whispers',
  'warmly',
  'sleepily',
  'sighs',
  'short pause',
  'long pause',
  'curious',
  'giggles',
] as const satisfies readonly (typeof BEDTIME_TAGS)[number][];
